<template>
  <div>
    <transition :name="$style.fade">
      <div v-if="showBanner" :class="$style.cookieBanner">
        <div :class="[$style.cookieContent, showOptions && $style.hasOptions]">
          <div v-if="!showOptions" :class="$style.cookieMessage">
            <h3 :class="$style.title">Cookies & Confidentialité</h3>

            <p :class="$style.description">
              Nous utilisons des cookies pour améliorer votre expérience sur
              notre site. En acceptant, vous nous aidez à personnaliser notre
              contenu pour vous.
            </p>
          </div>

          <div v-if="!showOptions" :class="$style.cookieActions">
            <button :class="$style.btnAccept" @click="acceptAllCookies">
              Accepter
            </button>

            <button :class="$style.btnOptions" @click="showMoreOptions">
              Plus d'options
            </button>
          </div>

          <button
            v-if="!showOptions"
            :class="$style.btnDeclineText"
            @click="refuseAllCookies"
          >
            Refuser les cookies
          </button>

          <div v-if="showOptions" :class="$style.cookieOptions">
            <div :class="$style.optionsGrid">
              <div :class="$style.optionItem">
                <div :class="$style.optionHeader">
                  <h4>Cookies essentiels</h4>

                  <span :class="$style.required">Requis</span>
                </div>

                <p>Nécessaires au fonctionnement du site web.</p>
              </div>

              <div :class="$style.optionItem">
                <div :class="$style.optionHeader">
                  <h4>Cookies de performance</h4>

                  <label :class="$style.toggle">
                    <input
                      v-model="cookiePreferences.performance"
                      type="checkbox"
                      :class="$style.toggleInput"
                    />

                    <span :class="$style.toggleSlider"></span>
                  </label>
                </div>

                <p>
                  Nous aident à comprendre comment vous utilisez notre site.
                </p>
              </div>

              <div :class="$style.optionItem">
                <div :class="$style.optionHeader">
                  <h4>Cookies d'analyse</h4>

                  <label :class="$style.toggle">
                    <input
                      v-model="cookiePreferences.analytics"
                      type="checkbox"
                      :class="$style.toggleInput"
                    />

                    <span :class="$style.toggleSlider"></span>
                  </label>
                </div>

                <p>
                  Collectent des informations sur l'utilisation du site de
                  manière anonyme.
                </p>
              </div>

              <div :class="$style.optionItem">
                <div :class="$style.optionHeader">
                  <h4>Cookies publicitaires</h4>

                  <label :class="$style.toggle">
                    <input
                      v-model="cookiePreferences.advertising"
                      type="checkbox"
                      :class="$style.toggleInput"
                    />

                    <span :class="$style.toggleSlider"></span>
                  </label>
                </div>

                <p>Utilisés pour vous proposer des publicités pertinentes.</p>
              </div>
            </div>

            <div :class="$style.optionsActions">
              <button :class="$style.btnSave" @click="savePreferences">
                Sauvegarder mes préférences
              </button>

              <button :class="$style.btnBack" @click="hideOptions">
                Retour
              </button>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { cookieConsentDataLayerEvent } from '@front/common/data-layer-events'
import { ref, onMounted, defineComponent } from '@vue/composition-api'

export default defineComponent({
  name: 'CookieBanner',
  setup() {
    const showBanner = ref(false)
    const showOptions = ref(false)
    const cookiePreferences = ref({
      essential: true, // Always true, cannot be disabled
      performance: true,
      analytics: true,
      advertising: true,
    })

    const checkCookieConsent = () => {
      const cookieConsent = localStorage.getItem('cookieConsent')

      if (cookieConsent) {
        const preferences = JSON.parse(cookieConsent)

        cookiePreferences.value = { ...cookiePreferences.value, ...preferences }
        showBanner.value = false
      } else {
        showBanner.value = true
      }
    }

    const refuseAllCookies = () => {
      cookiePreferences.value = {
        essential: true,
        performance: false,
        analytics: false,
        advertising: false,
      }
      localStorage.setItem(
        'cookieConsent',
        JSON.stringify(cookiePreferences.value),
      )
      showBanner.value = false
      cookieConsentDataLayerEvent(cookiePreferences.value)
    }

    const acceptAllCookies = () => {
      cookiePreferences.value = {
        essential: true,
        performance: true,
        analytics: true,
        advertising: true,
      }
      localStorage.setItem(
        'cookieConsent',
        JSON.stringify(cookiePreferences.value),
      )
      showBanner.value = false
      cookieConsentDataLayerEvent(cookiePreferences.value)
    }

    const showMoreOptions = () => {
      showOptions.value = true
    }

    const hideOptions = () => {
      showOptions.value = false
    }

    const savePreferences = () => {
      localStorage.setItem(
        'cookieConsent',
        JSON.stringify(cookiePreferences.value),
      )
      showBanner.value = false
      cookieConsentDataLayerEvent(cookiePreferences.value)
    }

    onMounted(() => {
      checkCookieConsent()
    })

    return {
      showBanner,
      showOptions,
      cookiePreferences,
      acceptAllCookies,
      showMoreOptions,
      hideOptions,
      savePreferences,
      refuseAllCookies,
    }
  },
})
</script>

<style lang="scss" module>
$border-radius: 30px;
$transition-speed: 0.3s;

.cookieBanner {
  font-family: $font-geomanist;
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: $color-background;
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.08);
  z-index: 1000;
  padding: 0;
  max-height: 100vh;
  overflow-y: auto;

  @media (max-width: 767px) {
    max-height: 90vh;
  }

  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.08), 0 -8px 25px rgba(0, 0, 0, 0.12);
}

.cookieContent {
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: space-between;
  padding: 1.5rem 1rem;

  @media (min-width: 768px) {
    flex-direction: row;
    align-items: center;
    padding: 2.5rem 3rem;
  }

  // When showing options, always use column layout for better mobile experience
  &.hasOptions {
    flex-direction: column;
    align-items: stretch;

    @media (min-width: 768px) {
      flex-direction: column;
      align-items: stretch;
    }
  }
}

.cookieMessage {
  margin-bottom: 1.5rem;
  max-width: 700px;

  @media (min-width: 768px) {
    margin-bottom: 0;
    margin-right: 2rem;
  }
}

.title {
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0 0 0.8rem 0;
  color: $color-primary;

  @media (min-width: 768px) {
    font-size: 1.5rem;
  }
}

.description {
  font-size: 0.9rem;
  line-height: 1.6;
  margin: 0;
  color: $color-subtitle;

  @media (min-width: 768px) {
    font-size: 1rem;
  }
}

.cookieActions {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  width: 100%;

  @media (min-width: 768px) {
    flex-direction: row;
    gap: 1rem;
    width: auto;
  }
}

.btnAccept,
.btnDecline,
.btnOptions {
  padding: 1rem 1.5rem;
  border: none;
  border-radius: $border-radius;
  font-weight: 500;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all $transition-speed ease;
  min-height: 44px; // Touch-friendly minimum height
  width: 100%;

  @media (min-width: 768px) {
    padding: 0.8rem 2rem;
    font-size: 1rem;
    width: auto;
  }

  &:focus {
    outline: none;
  }
}

.btnAccept {
  background-color: $color-primary;
  color: white;

  &:hover {
    background-color: lighten($color-primary, 15%);
    transform: translateY(-2px);
  }
}

.btnDecline {
  background-color: transparent;
  color: $color-primary;
  border: 1px solid $color-primary;

  &:hover {
    background-color: rgba($color-primary, 0.05);
    transform: translateY(-2px);
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: all 0.6s cubic-bezier(0.16, 1, 0.3, 1);
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
  transform: translateY(20px);
}

.cookieOptions {
  width: 100%;
  margin-top: 1.5rem;

  @media (max-width: 767px) {
    margin-top: 1rem;
  }
}

.optionsGrid {
  display: grid;
  gap: 1rem;
  margin-bottom: 2rem;

  @media (min-width: 768px) {
    gap: 1.5rem;
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 767px) {
    gap: 0.75rem;
    margin-bottom: 1.5rem;
  }
}

.optionItem {
  padding: 1rem;
  background-color: white;
  border-radius: 12px;
  border: 1px solid #e5e5e5;

  @media (min-width: 768px) {
    padding: 1.5rem;
  }

  @media (max-width: 767px) {
    padding: 1rem;
  }

  h4 {
    margin: 0 0 0.5rem 0;
    font-size: 1rem;
    font-weight: 600;
    color: $color-primary;

    @media (min-width: 768px) {
      font-size: 1.1rem;
    }
  }

  p {
    margin: 0;
    font-size: 0.85rem;
    color: #666;
    line-height: 1.4;

    @media (min-width: 768px) {
      font-size: 0.9rem;
    }
  }
}

.optionHeader {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.required {
  background-color: #e8f5e8;
  color: $color-input;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.7rem;
  font-weight: 500;
  white-space: nowrap;

  @media (min-width: 768px) {
    font-size: 0.6rem;
  }
}

.toggle {
  position: relative;
  display: inline-block;
  width: 50px;
  height: 24px;
  flex-shrink: 0;
}

.toggleInput {
  opacity: 0;
  width: 0;
  height: 0;

  &:checked + .toggleSlider {
    background-color: $color-secondary;

    &:before {
      transform: translateX(26px);
    }
  }
}

.toggleSlider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  transition: 0.3s;
  border-radius: 24px;

  &:before {
    position: absolute;
    content: '';
    height: 18px;
    width: 18px;
    left: 3px;
    bottom: 3px;
    background-color: white;
    transition: 0.3s;
    border-radius: 50%;
  }
}

.optionsActions {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  justify-content: center;

  @media (min-width: 768px) {
    flex-direction: row;
    gap: 1rem;
    flex-wrap: wrap;
  }

  @media (max-width: 767px) {
    gap: 0.5rem;
  }
}

.btnSave,
.btnBack {
  padding: 1rem 1.5rem;
  border: none;
  border-radius: $border-radius;
  font-weight: 500;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all $transition-speed ease;
  min-height: 44px; // Touch-friendly minimum height
  width: 100%;

  @media (min-width: 768px) {
    padding: 0.8rem 2rem;
    font-size: 1rem;
    width: auto;
  }

  &:focus {
    outline: none;
  }
}

.btnSave {
  background-color: $color-secondary;
  color: white;

  &:hover {
    background-color: darken($color-secondary, 10%);
    transform: translateY(-2px);
  }
}

.btnOptions,
.btnBack {
  background-color: transparent;
  color: $color-primary;
  border: 1px solid #ddd;

  &:hover {
    background-color: rgba($color-primary, 0.05);
    transform: translateY(-2px);
  }
}

.btnDeclineText {
  background: none;
  border: none;
  color: lighten($color-primary, 25%);
  text-decoration: underline;
  cursor: pointer;
  font-size: 0.8rem;
  padding: 0.6rem 1rem;
  min-height: 36px;
  width: 100%;
  max-width: 320px;
  margin: 0.5rem auto 0 auto;
  display: block;
  opacity: 0.6;
  transition: color $transition-speed, opacity $transition-speed;
  font-weight: normal;
  text-align: center;
  border-radius: 20px;
  @media (min-width: 768px) {
    padding: 0.2rem 1rem;
    font-size: 0.85rem;
    width: auto;
    margin: 0.5rem 0 0 0;
    display: inline-block;
  }
  &:hover {
    color: $color-primary;
    opacity: 1;
  }
}

@media (max-width: 375px) {
  .cookieContent.hasOptions {
    padding: 1rem 0.3rem;
  }
  .cookieOptions {
    margin-top: 0.5rem;
  }
  .optionsGrid {
    gap: 0.4rem;
    margin-bottom: 1rem;
  }
  .optionItem {
    padding: 0.6rem;
  }
  .optionHeader h4 {
    font-size: 0.9rem;
  }
  .optionItem p {
    font-size: 0.75rem;
  }
  .optionsActions {
    gap: 0.4rem;
  }
  .btnSave,
  .btnBack {
    padding: 0.6rem 1rem;
    font-size: 0.85rem;
    min-height: 36px;
  }
}
</style>
